/* ============================================
   Partners — Admin page to manage partner agencies
   Sub-module under Clientes
   ============================================ */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import { PageHeader } from '@/components/ui'
import { IconPlus } from '@/components/icons/Icons'

interface Partner {
  id: number
  email: string
  cliente_id: number | null
  empresa_nombre: string
  must_change_password: boolean
  leads_count: number
  conversaciones_count: number
  wazzup_status: string | null
  created_at: string
}

interface CatalogServicio {
  id: number
  nombre: string
  categoria: string
}

export default function Partners() {
  const api = useApi()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')

  // Modal state
  const [showResetModal, setShowResetModal] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)

  // Form state
  const [resetPassword, setResetPassword] = useState('')
  const [formSaving, setFormSaving] = useState(false)

  // Servicios whitelabel modal state
  const [showServiciosModal, setShowServiciosModal] = useState(false)
  const [selectedPartnerForServicios, setSelectedPartnerForServicios] = useState<Partner | null>(null)
  const [allServicios, setAllServicios] = useState<CatalogServicio[]>([])
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([])
  const [serviciosLoading, setServiciosLoading] = useState(false)
  const [serviciosSaving, setServiciosSaving] = useState(false)

  const { data: partners = [], isLoading: loading, refrescar: loadData } = useCachedData(
    ['partners'],
    () => api.get<Partner[]>('/api/auth/partners')
      .then(d => (Array.isArray(d) ? d : []))
      .catch(() => {
        showToast('Error cargando datos', 'error')
        return [] as Partner[]
      }),
  )

  // Filter
  const filtered = partners.filter((p) => {
    const q = search.toLowerCase()
    return (
      p.email.toLowerCase().includes(q) ||
      p.empresa_nombre.toLowerCase().includes(q)
    )
  })

  // Delete partner
  const handleDelete = async (partner: Partner) => {
    if (!confirm(`¿Eliminar la cuenta de ${partner.email}? Los leads y conversaciones existentes se mantendrán.`)) return

    try {
      await api.del(`/api/auth/partners/${partner.id}`)
      showToast('Partner eliminado', 'success')
      loadData()
    } catch {
      showToast('Error al eliminar', 'error')
    }
  }

  // Reset password
  const handleResetPassword = async () => {
    if (!selectedPartner || !resetPassword) return
    if (resetPassword.length < 6) {
      showToast('Mínimo 6 caracteres', 'error')
      return
    }

    setFormSaving(true)
    try {
      await api.post(`/api/auth/partners/${selectedPartner.id}/reset-password`, {
        password: resetPassword,
      })
      showToast('Contraseña actualizada. El partner deberá cambiarla al iniciar sesión.', 'success')
      setShowResetModal(false)
      setResetPassword('')
      setSelectedPartner(null)
      loadData()
    } catch {
      showToast('Error al actualizar contraseña', 'error')
    } finally {
      setFormSaving(false)
    }
  }

  // Open whitelabel services management modal
  const handleOpenServicios = async (partner: Partner) => {
    setSelectedPartnerForServicios(partner)
    setShowServiciosModal(true)
    setServiciosLoading(true)
    try {
      let services = allServicios
      if (services.length === 0) {
        const res = await api.get<CatalogServicio[]>('/api/servicios')
        services = Array.isArray(res) ? res : []
        setAllServicios(services)
      }

      const assigned = await api.get<{ serviceIds: number[] }>(`/api/auth/partners/${partner.id}/servicios`)
      setSelectedServiceIds(assigned?.serviceIds || [])
    } catch {
      showToast('Error al cargar servicios del partner', 'error')
    } finally {
      setServiciosLoading(false)
    }
  }

  // Toggle service selection
  const handleToggleService = (serviceId: number) => {
    setSelectedServiceIds(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    )
  }

  // Save selected whitelabel services
  const handleSaveServicios = async () => {
    if (!selectedPartnerForServicios) return
    setServiciosSaving(true)
    try {
      await api.post(`/api/auth/partners/${selectedPartnerForServicios.id}/servicios`, {
        serviceIds: selectedServiceIds,
      })
      showToast('Servicios de marca blanca asignados correctamente', 'success')
      setShowServiciosModal(false)
      setSelectedPartnerForServicios(null)
    } catch {
      showToast('Error al guardar servicios de marca blanca', 'error')
    } finally {
      setServiciosSaving(false)
    }
  }

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
    let pw = ''
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)]
    return pw
  }

  const formatDate = (d: string) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  if (loading) {
    return (
      <div className="partners-page">
        <div className="partners-page__loading">
          <div className="auth-spinner" />
          <p>Cargando partners...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="partners-page">
      <PageHeader
        breadcrumbs={[{ label: 'Clientes' }, { label: 'Partners' }]}
        meta={`${partners.length} agencia${partners.length !== 1 ? 's' : ''}`}
        actions={
          <button className="btn btn-primary" onClick={() => navigate('/clientes/partners/nuevo')}>
            <IconPlus size={16} /> Nuevo partner
          </button>
        }
      />

      {/* Search */}
      {partners.length > 0 && (
        <div className="partners-page__search-bar">
          <input
            type="text"
            placeholder="Buscar por email o empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="partners-page__search-input"
          />
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="partners-page__empty">
          <div className="partners-page__empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <h3>{search ? 'Sin resultados' : 'No hay partners'}</h3>
          <p>{search ? 'Prueba con otro término de búsqueda' : 'Crea el primer partner para que las agencias puedan gestionar sus leads'}</p>
          {!search && (
            <button className="partners-page__btn partners-page__btn--primary" onClick={() => navigate('/clientes/partners/nuevo')}>
              + Nuevo partner
            </button>
          )}
        </div>
      ) : (
        <div className="partners-page__table-wrapper">
          <table className="partners-page__table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Email</th>
                <th>WhatsApp</th>
                <th>Leads</th>
                <th>Conversaciones</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td className="partners-page__cell-empresa">{p.empresa_nombre}</td>
                  <td>
                    <span className="partners-page__email">{p.email}</span>
                    {p.must_change_password && (
                      <span className="partners-page__badge partners-page__badge--warn" title="Debe cambiar contraseña"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg></span>
                    )}
                  </td>
                  <td>
                    {p.wazzup_status === 'activo' ? (
                      <span className="partners-page__badge partners-page__badge--green"><svg width="8" height="8" viewBox="0 0 8 8" style={{ marginRight: 5, verticalAlign: 'middle' }}><circle cx="4" cy="4" r="4" fill="#22c55e" /></svg>Conectado</span>
                    ) : (
                      <span className="partners-page__badge partners-page__badge--gray">Sin conectar</span>
                    )}
                  </td>
                  <td className="partners-page__cell-num">{p.leads_count}</td>
                  <td className="partners-page__cell-num">{p.conversaciones_count}</td>
                  <td className="partners-page__cell-date">{formatDate(p.created_at)}</td>
                  <td className="partners-page__cell-actions">
                    <button
                      className="partners-page__action-btn"
                      title="Servicios Marca Blanca"
                      onClick={() => handleOpenServicios(p)}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                    </button>
                    <button
                      className="partners-page__action-btn"
                      title="Resetear contraseña"
                      onClick={() => {
                        setSelectedPartner(p)
                        setResetPassword(generatePassword())
                        setShowResetModal(true)
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
                    </button>
                    <button
                      className="partners-page__action-btn partners-page__action-btn--danger"
                      title="Eliminar"
                      onClick={() => handleDelete(p)}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && selectedPartner && (
        <Modal title={`Resetear contraseña — ${selectedPartner.email}`} isOpen={showResetModal} onClose={() => setShowResetModal(false)}>
          <div className="partners-page__modal-form">
            <div className="partners-page__form-group">
              <label>Nueva contraseña</label>
              <div className="partners-page__input-row">
                <input
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="partners-page__input partners-page__input--mono"
                />
                <button
                  type="button"
                  className="partners-page__btn partners-page__btn--secondary partners-page__btn--sm"
                  onClick={() => setResetPassword(generatePassword())}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                </button>
              </div>
              <span className="partners-page__form-hint">El partner deberá cambiarla al iniciar sesión</span>
            </div>

            <div className="partners-page__modal-actions">
              <button className="partners-page__btn partners-page__btn--ghost" onClick={() => setShowResetModal(false)}>
                Cancelar
              </button>
              <button
                className="partners-page__btn partners-page__btn--primary"
                onClick={handleResetPassword}
                disabled={formSaving}
              >
                {formSaving ? 'Guardando...' : 'Actualizar contraseña'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Whitelabel Services Modal */}
      {showServiciosModal && selectedPartnerForServicios && (
        <Modal
          title={`Servicios Marca Blanca — ${selectedPartnerForServicios.empresa_nombre}`}
          isOpen={showServiciosModal}
          onClose={() => setShowServiciosModal(false)}
        >
          <div className="partners-page__modal-form">
            <p className="partners-page__form-intro">
              Selecciona los servicios del catálogo que este partner puede comercializar bajo su propia marca blanca:
            </p>

            {serviciosLoading ? (
              <div className="partners-page__loading-services" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', gap: '1rem' }}>
                <div className="auth-spinner" />
                <p style={{ color: 'var(--text-secondary)' }}>Cargando servicios disponibles...</p>
              </div>
            ) : allServicios.length === 0 ? (
              <p className="partners-page__no-services" style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No hay servicios en el catálogo principal.</p>
            ) : (
              <div className="partners-page__services-list" style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '4px', display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '1.5rem' }}>
                {Array.from(new Set(allServicios.map(s => s.categoria))).map(category => (
                  <div key={category} className="partners-page__service-category-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <h4 className="partners-page__service-category-title" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary)', borderBottom: '1px solid var(--border)', paddingBottom: '4px', margin: 0 }}>{category}</h4>
                    <div className="partners-page__service-items-grid" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {allServicios
                        .filter(s => s.categoria === category)
                        .map(s => {
                          const isChecked = selectedServiceIds.includes(s.id);
                          return (
                            <label
                              key={s.id}
                              className={`partners-page__service-checkbox-item ${isChecked ? 'partners-page__service-checkbox-item--active' : ''}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                background: isChecked ? 'color-mix(in srgb, var(--primary-light) 8%, transparent)' : 'var(--veil-03)',
                                border: isChecked ? '1px solid var(--primary)' : '1px solid var(--border)',
                                cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleToggleService(s.id)}
                                style={{ cursor: 'pointer' }}
                              />
                              <div className="partners-page__service-checkbox-content">
                                <span className="partners-page__service-name" style={{ fontSize: '0.9rem', color: isChecked ? 'var(--text-primary, #ffffff)' : 'var(--text-secondary, #b2c0cc)', fontWeight: isChecked ? 600 : 400 }}>{s.nombre}</span>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="partners-page__modal-actions">
              <button
                className="partners-page__btn partners-page__btn--ghost"
                onClick={() => setShowServiciosModal(false)}
              >
                Cancelar
              </button>
              <button
                className="partners-page__btn partners-page__btn--primary"
                onClick={handleSaveServicios}
                disabled={serviciosSaving || serviciosLoading}
              >
                {serviciosSaving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
